<template>
  <Suspense>
    <!-- That's what will be displayed WHEN all promises are resolved -->
    <template #default>
      <div class="content-grid content-grid--subpage">
        <Breadcrumbs :title="t(`routes.admin.children.organizations.view.name`)"/>
        <div class="content-grid__header">
          <BackLinkTitle :title="t('routes.admin.children.organizations.view.name')"/>
          <p v-if="organization" class="util--text-grey">
            {{ `${t('organization.modifiedOn')} ${organization.lastModified.toLocalDateTimeString()}` }}
          </p>
        </div>
        <Card v-if="organization" :title="organization.name">
          <OrganizationForm v-model="organization" @submit="handleSubmit" @on-image-change="onImageChange"/>
        </Card>

        <MemberTable v-if="organization"
                     :card-link-path="addMemberPath"
                     :hide-card-link="organization.type == OrganizationType.Corporate && organization.members.length > 0"
                     :is-loading="organizationIsLoading"
                     :members="organization.members"
                     :organization-id="organization.id.toString()"/>

        <ContactTable v-if="organization && organization.type === OrganizationType.Corporate"
                      :card-link-path="addContactPath"
                      :contacts="organization.contacts"
                      :is-loading="organizationIsLoading"
                      :organization-id="organization.id.toString()"/>

        <InvoiceTable v-if="organization"
                      :invoices="organizationInvoices"
                      :is-loading="organizationIsLoading"/>
      </div>
    </template>
    <!-- That's what will be displayed UNTIL all promises are resolved -->
    <template #fallback>
      <Loader/>
    </template>
  </Suspense>
</template>

<script lang="ts" setup>
import Loader from "@/components/layout/Loader.vue";
import {useI18n} from "vue3-i18n";
import Breadcrumbs from "@/components/layout/Breadcrumbs.vue";
import BackLinkTitle from "@/components/layout/BackLinkTitle.vue";
import {useMemberStore} from "@/stores/memberStore";
import {useOrganizationService} from "@/inversify.config";
import {computed, onMounted, ref} from "vue";
import Card from "@/components/layout/Card.vue";
import MemberTable from "@/components/tables/MemberTable.vue";
import ContactTable from "@/components/tables/ContactTable.vue";
import InvoiceTable from "@/components/tables/InvoiceTable.vue";
import {IFullOrganization} from "@/types/entities/organization";
import {notifyError, notifySuccess} from "@/notify";
import {getFileFromUrl} from "@/helpers";
import {Invoice} from "@/types/entities";
import {OrganizationType, Role} from "@/types";
import OrganizationForm from "@/components/forms/OrganizationForm.vue";
import {IEditOrganizationRequest} from "@/types/requests";

// eslint-disable-next-line no-undef
const props = defineProps<{
  organizationId: string;
}>();

const {t} = useI18n()
const memberStore = useMemberStore();
const organizationService = useOrganizationService();

const organizationLogo = ref<File>()
const organization = ref<IFullOrganization>();
const organizationIsLoading = ref(false);

let organizationInvoices = computed(() => {
  if (!organization.value)
    return []

  if (!memberStore.hasRole(Role.RegularSyndicateCoOwner))
    return getSubscriptionInvoices();
  return []
})

const addMemberPath = {name: 'admin.children.organizations.addMember', params: {organizationId: props.organizationId}};
const addContactPath = {
  name: 'admin.children.organizations.addContact',
  params: {organizationId: props.organizationId}
};

onMounted(() => {
  loadOrganization()
})

async function loadOrganization() {
  organizationIsLoading.value = true;

  let fullOrganization = await organizationService.getOrganizationWithId(props.organizationId)
  if (fullOrganization) {
    organization.value = fullOrganization;
    if (fullOrganization.logo)
      organizationLogo.value = await getFileFromUrl(fullOrganization.logo, fullOrganization.logo)
  }
  organizationIsLoading.value = false;
}

function getSubscriptionInvoices(): Invoice[] {
  if (!organization.value?.subscription.invoices?.length)
    return [];
  organization.value?.subscription.invoices?.forEach(x => {
    x.description = `${t(`products.membershipPlan`)} : ${x.createdAt}`
  })
  return organization.value?.subscription.invoices;
}

async function onImageChange(file: File) {
  organizationLogo.value = file
}

async function handleSubmit(organization: IFullOrganization) {
  let request: IEditOrganizationRequest = {
    id: organization.id,
    type: organization.type,
    logo: organizationLogo.value,
    email: organization.email,
    phoneNumber: organization.phoneNumber,
    phoneExtension: organization.phoneExtension,
    apartment: organization.apartment,
    street: organization.street,
    city: organization.city,
    zipCode: organization.zipCode,
    enterpriseNumber: organization.enterpriseNumber,
    website: organization.website,
    numberOfUnits: organization.numberOfUnits,
    yearsOfExperience: organization.yearsOfExperience,
    activityAreas: organization.activityAreas,
    descriptionFr: organization.descriptionFr,
    descriptionEn: organization.descriptionEn,
    hasContingencyFundCertification: organization.hasContingencyFundCertification
  };
  let succeededOrNotResponse = await organizationService.editOrganizationWithId(props.organizationId, request)
  if (succeededOrNotResponse && succeededOrNotResponse.succeeded) {
    notifySuccess(t('validation.organization.update.success'))
  } else {
    let errorMessages = succeededOrNotResponse.getErrorMessages(
        'validation.organization.update', 'validation.organization.update.errorOccured');
    notifyError(errorMessages[0])
  }
}
</script>
